/**
 * Host (TV) end-game view. Winner banner, the final board, and a race-bet
 * resolution table (all colors revealed — the service denormalizes them into
 * public state on game end).
 */

import { Button } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PLAYER_COLOR_HEX, type Room } from '@mdev/common'
import { rematch } from '../../lobby/service'
import { BoardStrip } from './BoardStrip'
import { CAMEL_HEX, CU, CU_DISPLAY, CU_HEADING } from '../theme'
import { CamelGlyph } from './CamelGlyph'
import { LegReviewPanel } from './LegReviewPanel'
import { Coins, Panel, PanelTitle, Wordmark } from './pieces'
import type { CamelUpSettings, CamelUpState } from '../module'

interface Props {
  room: Room<CamelUpSettings>
  state: CamelUpState
}

function delta(n: number) {
  return n >= 0 ? `+${n}` : `${n}`
}

export function HostFinishedView(props: Props) {
  const { room, state } = props
  const winnerUid = state.winner?.uid
  const winner = winnerUid ? state.players[winnerUid] : null
  const review = state.raceReview

  const navigate = useNavigate()
  const [rematchPending, setRematchPending] = useState(false)
  async function onPlayAgain() {
    if (room.nextCode) {
      navigate(`/games/${room.gameId}/host/${room.nextCode}`)
      return
    }
    setRematchPending(true)
    try {
      const { code } = await rematch(room.code, room.gameId, room.settings)
      navigate(`/games/${room.gameId}/host/${code}`)
    } catch {
      setRematchPending(false)
    }
  }

  return (
    <div style={{ width: '100%', minHeight: '100%', background: CU.pageBg, padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <Wordmark
          size={36}
          trailing={<span style={{ fontFamily: CU_HEADING, fontSize: 16, color: CU.inkSoft }}>Room {room.code}</span>}
        />
        <Button
          color="yellow"
          size="md"
          leftSection={<IconRefresh size={18} />}
          loading={rematchPending}
          onClick={onPlayAgain}
        >
          {room.nextCode ? 'Go to next game' : 'Play again'}
        </Button>
      </div>

      {winner && (
        <div
          style={{
            borderRadius: 18,
            padding: '20px 24px',
            background: `linear-gradient(135deg, ${CU.coin}, ${CU.coinDeep})`,
            border: `2px solid ${CU.coinDeep}`,
            color: '#3a2a14',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            boxShadow: CU.shadow,
          }}
        >
          <span style={{ fontSize: 44 }}>🏆</span>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontFamily: CU_DISPLAY, fontSize: 32, lineHeight: 1 }}>
              {winner.avatar} {winner.displayName}
            </span>
            <span style={{ fontFamily: CU_HEADING, fontSize: 16 }}>
              takes the purse — {state.winner!.coins} coins
            </span>
          </div>
        </div>
      )}

      <BoardStrip state={state} />

      {review && (
        <Panel pad={20} style={{ boxShadow: CU.shadow }}>
          <PanelTitle>Race results</PanelTitle>
          <div style={{ display: 'flex', gap: 28, marginBottom: 18 }}>
            <ResultBadge label="Race winner" color={review.raceWinner} accent={CU.brass} />
            <ResultBadge label="Race loser" color={review.raceLoser} accent={CU.inkSoft} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {review.rows.map((row) => {
              const p = state.players[row.uid]
              return (
                <div
                  key={row.uid}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: CU.surfaceSolid,
                    borderLeft: `4px solid ${PLAYER_COLOR_HEX[p.color]}`,
                  }}
                >
                  <span style={{ width: 140, display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
                    <span style={{ fontSize: 18 }}>{p.avatar}</span>
                    <span style={{ fontFamily: CU_HEADING, fontWeight: 600, color: CU.ink }}>{p.displayName}</span>
                  </span>
                  <span style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {row.bets.length === 0 ? (
                      <span style={{ fontSize: 12, color: CU.inkSoft }}>no race bets</span>
                    ) : (
                      row.bets.map((b, i) => (
                        <span
                          key={i}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontFamily: CU_HEADING,
                            color: CAMEL_HEX[b.color].ink,
                            background: `linear-gradient(135deg, ${CAMEL_HEX[b.color].light}, ${CAMEL_HEX[b.color].dark})`,
                            opacity: b.correct ? 1 : 0.65,
                          }}
                        >
                          {b.pile === 'winner' ? 'W' : 'L'} · {b.color} · {delta(b.payout)}
                        </span>
                      ))
                    )}
                  </span>
                  <span style={{ width: 60, textAlign: 'right', fontFamily: CU_HEADING, fontWeight: 700, color: row.raceBetCoins >= 0 ? '#1DA64A' : CU.accent, flex: '0 0 auto' }}>
                    {delta(row.raceBetCoins)}
                  </span>
                  <span style={{ width: 64, display: 'flex', justifyContent: 'flex-end', flex: '0 0 auto' }}>
                    <Coins amount={row.finalCoins} />
                  </span>
                </div>
              )
            })}
          </div>
        </Panel>
      )}

      {/* Final leg coin payouts — the leg is scored when the race ends, so
          surface that breakdown here too (not just on a normal leg break). */}
      <LegReviewPanel state={state} />
    </div>
  )
}

function ResultBadge({ label, color, accent }: { label: string; color: CamelUpState['finished'][number]; accent: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontFamily: CU_HEADING, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: CU.inkSoft }}>
        {label}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CamelGlyph color={color} size={30} />
        <span style={{ fontFamily: CU_DISPLAY, fontSize: 22, textTransform: 'capitalize', color: accent }}>{color}</span>
      </span>
    </div>
  )
}
